<template>
  <div>
    <div class="flex flex-wrap w-full border-b-2 border-gray-500">
      <template
        v-for="(orderFactory, orderFactoryIndex) in orderFactories"
        :key="`${orderFactory.id}_${orderFactoryIndex}`"
      >
        <button
          @click="handleFactoryUpdate(orderFactory)"
          :class="[
            'bg-primary text-black font-medium py-1.5 px-3 shadow-inner',
            {
              'bg-primary text-white shadow-none': activeFactory.id === orderFactory.id,
            },
            orderFactoryIndex === 0 ? 'rounded-tl' : '',
            orderFactoryIndex === orderFactories.length - 1 ? 'rounded-tr' : '',
          ]"
        >
          {{ orderFactory.name }}
        </button>
      </template>
    </div>

    <div
      class="relative container-fluid mt-3 bg-gray-200 p-4"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="handleContainerDragLeave"
    >
      <!-- Header -->
      <div
        class="flex justify-end items-center mb-3 gap-1 flex-wrap"
        v-if="!sharePointDbSettingsNotFound"
      >
        <button
          class="bg-primary text-white px-3 py-1.5 rounded hover:bg-emerald-600 transition"
          @click="fileInput?.click()"
        >
          Upload or drop
        </button>
        <button
          class="bg-primary text-white px-3 py-1.5 rounded hover:bg-emerald-600 transition"
          @click="openCreateFolderModal"
        >
          Create Folder
        </button>
      </div>

      <!-- Breadcrumbs -->
      <nav v-if="driveBreadCrumbs?.length" class="bg-white rounded-lg p-2.5 shadow mb-3">
        <ol class="flex flex-wrap">
          <li
            v-for="(breadcrumb, index) in driveBreadCrumbs"
            :key="breadcrumb.name + '_' + index"
            class="flex items-center"
          >
            <template v-if="index > 0">
              <span class="mx-2 text-gray-500">/</span>
            </template>
            <button
              :class="[
                breadcrumb.isActive
                  ? 'text-gray-800 font-bold'
                  : 'text-primary hover:underline cursor-pointer',
              ]"
              @click="
                !breadcrumb.isActive ? getSharePointFilesAtPath(breadcrumb.path) : () => false
              "
            >
              <span v-if="breadcrumb.name === '/'"> {{ breadcrumb.name }} Home </span>
              <span v-else>
                {{ breadcrumb.name }}
              </span>
            </button>
          </li>
        </ol>
      </nav>

      <!-- Folders -->
      <div v-if="sharePointDbSettingsNotFound" class="flex justify-center items-center">
        <h5 class="text-center">
          Unable to access the SharePoint drive because the necessary SharePoint factory settings
          are not configured.
        </h5>
      </div>
      <div
        v-else
        class="container-fluid mt-3 bg-gray-200 relative"
        @dragover.prevent="handleContainerDragOver"
        @dragleave.prevent="handleContainerDragLeave"
        @drop.prevent="handleContainerDrop"
        :class="{
          'border-2 border-dashed border-emerald-500 bg-emerald-500/40':
            isDragging && !isMovingItems,
        }"
      >
        <!-- Loader -->
        <div
          v-if="isLoading || isOperationLoading"
          class="absolute inset-0 flex justify-center items-center bg-white/80 z-10"
        >
          <div
            class="w-12 h-12 rounded-full border-4 border-emerald-500/70 border-t-transparent animate-spin"
          ></div>
        </div>
        <div
          class="relative"
          ref="fileContainer"
          @mousedown="handleMouseDown"
          @mousemove="updateSelection"
          @contextmenu.prevent="handleContainerContextMenu($event)"
          :class="{
            'border-2 border-dashed border-emerald-500 bg-emerald-500/40':
              isDragging && !isMovingItems,
          }"
        >
          <div class="flex flex-wrap mb-4 gap-2">
            <template v-if="driveItems?.length > 0">
              <div
                v-for="driveItem in driveItems"
                :key="driveItem.name"
                draggable="true"
                class="drive-item relative flex flex-col items-center justify-center p-3 me-3 mb-3 rounded-lg transition-all duration-300 cursor-pointer"
                :class="[
                  'flex-1 basis-[120px] max-w-[150px] w-full',
                  {
                    'bg-teal-50 border border-teal-600': selectedFiles.includes(driveItem),
                  },
                  { 'hover:bg-white': !selectedFiles.includes(driveItem) },
                  {
                    'bg-emerald-500/20 border-2 border-dashed border-emerald-500':
                      dragOverItem === driveItem && driveItem.type === 'folder',
                  },
                ]"
                @dblclick="handleItemDoubleClick(driveItem)"
                @contextmenu.prevent="handleContextMenu($event, driveItem)"
                @click="handleItemClick($event, driveItem)"
                @dragstart="handleDragStart($event, driveItem)"
                @dragover.prevent="handleDragOver($event, driveItem)"
                @dragleave.prevent="handleDragLeave($event, driveItem)"
                @drop.prevent="handleDrop($event, driveItem)"
                @dragend="handleDragEnd"
                :title="`${driveItem.name}, ${driveItem.size}MB`"
              >
                <button
                  class="item-menu hidden items-end p-0 border-0 text-gray-600 hover:text-gray-900 absolute top-[5px] right-[5px]"
                  @click.stop="handleContextMenu($event, driveItem)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    class="bi bi-three-dots-vertical"
                    viewBox="0 0 16 16"
                  >
                    <path
                      d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"
                    />
                  </svg>
                </button>
                <img
                  :src="getIcon(driveItem)"
                  alt="Icon"
                  class="w-[70px] h-[70px] object-cover bg-none"
                />
                <p
                  class="text-center mb-0 overflow-hidden text-ellipsis max-w-full text-sm line-clamp-2"
                >
                  {{ driveItem.name }}
                </p>
              </div>
            </template>
            <div
              v-else
              class="w-full flex justify-center items-center min-h-[120px] text-emerald-500"
            >
              <h2>Drop files here</h2>
            </div>
          </div>

          <div
            v-if="isSelecting"
            class="absolute bg-blue-500/30 border border-blue-500 pointer-events-none"
            :style="{
              left: selectionBox.left + 'px',
              top: selectionBox.top + 'px',
              width: selectionBox.width + 'px',
              height: selectionBox.height + 'px',
            }"
          ></div>
        </div>
      </div>

      <!-- Context Menu -->
      <div
        v-if="contextMenu.visible"
        tabindex="-1"
        ref="contextMenuRef"
        :style="contextMenu.style"
        class="fixed bg-white/90 border border-gray-300 shadow-md z-10 w-30 rounded-lg overflow-hidden"
      >
        <ul class="list-none p-0 m-0">
          <li
            class="px-2.5 py-1.5 cursor-pointer hover:bg-emerald-500 hover:text-white"
            @click="refreshDirectory"
          >
            Refresh
          </li>
          <li
            v-if="contextMenu.item"
            class="px-2.5 py-1.5 cursor-pointer hover:bg-emerald-500 hover:text-white"
            @click="handleDriveItemAction('cut')"
          >
            Cut
          </li>
          <li
            v-if="contextMenu.item"
            class="px-2.5 py-1.5 cursor-pointer hover:bg-emerald-500 hover:text-white"
            @click="handleDriveItemAction('copy')"
          >
            Copy
          </li>
          <li
            v-if="itemActionInfo && ['cut', 'copy'].includes(itemActionInfo.action)"
            class="px-2.5 py-1.5 cursor-pointer hover:bg-emerald-500 hover:text-white"
            @click="handleDriveItemAction('paste')"
          >
            Paste
          </li>
          <li
            v-if="contextMenu.item && !Array.isArray(contextMenu.item)"
            class="px-2.5 py-1.5 cursor-pointer hover:bg-emerald-500 hover:text-white"
            @click="openRenameModal"
          >
            Rename
          </li>
          <li
            v-if="contextMenu.item"
            class="px-2.5 py-1.5 cursor-pointer hover:bg-emerald-500 hover:text-white"
          >
            <a @click="showFileLogs(contextMenu.item)" class="text-inherit no-underline">
              File Logs
            </a>
          </li>
          <li
            v-if="contextMenu.item"
            class="px-2.5 py-1.5 cursor-pointer hover:bg-emerald-500 hover:text-white"
            @click="showDeleteConfirmModal"
          >
            Delete
          </li>
          <li
            v-if="
              contextMenu.item &&
              !Array.isArray(contextMenu.item) &&
              contextMenu.item.type === 'file' &&
              contextMenu.item.download_url
            "
            class="px-2.5 py-1.5 cursor-pointer hover:bg-emerald-500 hover:text-white"
          >
            <a :href="contextMenu.item.download_url" download class="text-inherit no-underline">
              Download
            </a>
          </li>
        </ul>
      </div>

      <!-- File Input (hidden) -->
      <input
        ref="fileInput"
        type="file"
        multiple
        class="hidden"
        @change="handleSharePointFilesChangeEvent"
      />

      <!-- Modals -->
      <!-- Create Folder Modal -->
      <Transition name="fade">
        <Dialog
          v-if="showCreateFolderModal"
          title="Create New Folder"
          :showCloseButton="true"
          :showFooter="true"
          :showSaveButton="true"
          :showCancelButton="true"
          :sm="true"
          @closeEvent="
            () => {
              newFolderName = '';
              showCreateFolderModal = false;
            }
          "
          @cancelEvent="
            () => {
              newFolderName = '';
              showCreateFolderModal = false;
            }
          "
          @saveEvent="handleDriveItemAction('new_folder')"
          :saveButtonProps="{
            disabled: !newFolderName,
          }"
        >
          <template #modal-content>
            <input
              v-model="newFolderName"
              placeholder="Enter folder name"
              class="w-full border rounded p-2 mt-2"
            />
          </template>
        </Dialog>
        <!-- <div class="bg-white rounded-lg p-4 w-full max-w-md">
          <h3 class="text-lg font-medium mb-4">Create New Folder</h3>

          <div class="flex justify-end gap-2">
            <button
              @click="showCreateFolderModal = false"
              class="px-4 py-2 border rounded hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              @click="handleDriveItemAction('new_folder')"
              class="px-4 py-2 bg-emerald-500 text-white rounded hover:bg-emerald-600"
            >
              Create
            </button>
          </div>
        </div>
      </div> -->
      </Transition>

      <!-- Confirmation Dialog -->
      <div
        v-if="showDeleteConfirm"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[100030]"
      >
        <div class="bg-white rounded-lg p-4 w-full max-w-md">
          <h3 class="text-lg font-medium mb-4">Confirm Delete</h3>
          <p v-if="Array.isArray(contextMenu.item) && contextMenu.item.length > 1">
            Are you sure you want to delete these
            {{ contextMenu.item.length }} items?
          </p>
          <p v-else>Are you sure you want to delete this item?</p>
          <div class="flex justify-end gap-2 mt-4">
            <button
              @click="showDeleteConfirm = false"
              class="px-4 py-2 border rounded hover:bg-gray-100"
              :disabled="isOperationLoading"
            >
              Cancel
            </button>
            <button
              @click="handleDriveItemAction('delete')"
              class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              :disabled="isOperationLoading"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <!-- Upload Cancel Confirm -->
      <div
        v-if="showUploadCancelConfirm"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[10030]"
      >
        <div class="bg-white rounded-lg p-4 w-full max-w-md">
          <h3 class="text-lg font-medium mb-4">Upload in progress!</h3>
          <p>Current uploading files will be cancelled! Are you sure you want to leave site?</p>
          <div class="flex justify-end gap-2 mt-4">
            <button
              @click="showUploadCancelConfirm = false"
              class="px-4 py-2 border rounded hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              @click="handleUploadCancelConfirm(true)"
              class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>

      <!-- Rename Dialog -->
      <div
        v-if="showRenameModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[10030]"
      >
        <div class="bg-white rounded-lg p-4 w-full max-w-md">
          <h3 class="text-lg font-medium mb-4">Rename Item</h3>
          <input
            v-model="driveItemNewName"
            placeholder="Enter new file name"
            class="w-full border rounded p-2 mb-4"
          />
          <div class="flex justify-end gap-2">
            <button
              @click="showRenameModal = false"
              class="px-4 py-2 border rounded hover:bg-gray-100"
              :disabled="isOperationLoading"
            >
              Cancel
            </button>
            <button
              @click="renameItem"
              class="px-4 py-2 bg-emerald-500 text-white rounded hover:bg-emerald-600"
              :disabled="isOperationLoading"
            >
              Rename
            </button>
          </div>
        </div>
      </div>

      <!-- Conflict Modal -->
      <div
        v-if="showConflictModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[10030]"
      >
        <div class="bg-white rounded-lg p-4 w-full max-w-md">
          <h3 class="text-lg font-medium mb-4">File Conflict Detected</h3>
          <p>One or more files with the same name already exist in the destination folder.</p>
          <p>Would you like to rename the files automatically to avoid conflicts?</p>
          <div class="flex justify-end gap-2 mt-4">
            <button
              @click="resolveConflict(false)"
              class="px-4 py-2 border rounded hover:bg-gray-100"
            >
              Cancel Operation
            </button>
            <button
              @click="resolveConflict(true)"
              class="px-4 py-2 bg-emerald-500 text-white rounded hover:bg-emerald-600"
            >
              Rename Files
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload Progress -->
    <div
      v-if="Object.keys(uploadProgress).length > 0 || preparingUploads.length > 0"
      class="fixed bottom-5 right-5 w-[300px] max-h-[200px] overflow-y-auto bg-white border border-gray-300 rounded p-2.5 shadow-md"
    >
      <div class="flex justify-between pb-2">
        <h5 class="text-base font-medium">Upload Progress:</h5>
      </div>
      <div v-for="(progress, fileName) in uploadProgress" :key="fileName" class="mb-2">
        <UploadProgressBar
          :progress="progress"
          :file-name="String(fileName)"
          :preparing="uploadPreparation[fileName]"
        />
        <button
          @click="cancelUpload(String(fileName))"
          class="ml-2.5 bg-red-500 text-white border-none rounded px-2 py-0.5 hover:bg-red-400 float-right"
        >
          Cancel
        </button>
      </div>
      <div v-for="item in preparingUploads" :key="item.fileName" class="mb-2">
        <UploadProgressBar :progress="0" :file-name="String(item.fileName)" :preparing="true" />
        <button
          @click="cancelUpload(String(item.fileName))"
          class="ml-2.5 bg-red-500 text-white border-none rounded px-2 py-0.5 hover:bg-red-400 float-right"
        >
          Cancel
        </button>
      </div>
    </div>

    <!-- Multi-file Upload Progress -->
    <div
      v-if="multiFileUpload.inProgress"
      class="fixed bottom-[230px] right-5 w-[300px] bg-white border border-gray-300 rounded p-2.5 shadow-md z-10"
    >
      <h5 class="text-base font-medium">
        Files {{ multiFileUpload.completed }} of {{ multiFileUpload.total }} uploading
      </h5>
      <div class="w-full h-5 bg-gray-100 rounded-full overflow-hidden mt-1.5">
        <div
          class="h-full bg-emerald-500 transition-all duration-300"
          :style="{
            width: `${(multiFileUpload.completed / multiFileUpload.total) * 100}%`,
          }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Dialog from '@/components/Shared/Dialog.vue';
import { showToast } from '@/composables/swal';
import { useSharepointFiles } from '@/composables/useSharepointFiles';
import type { DriveItem, Factory, FactoryId } from '@/types/sharepoint';
import axios from 'axios';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import UploadProgressBar from './components/UploadProgressBar.vue';

// Props
const props = defineProps<{
  order?: any;
  orderId: FactoryId;
  orderFactories: Factory[];
}>();

//Events Emits
const emits = defineEmits([
  'show-logs',
  'loading-logs',
]);

// Refs
const fileInput = ref<HTMLInputElement | null>(null);
const fileContainer = ref<HTMLDivElement | null>(null);
const isDragging = ref(false);

// Use the composable
const {
  isLoading,
  isOperationLoading,
  driveItems,
  driveItemsParentInfo,
  driveBreadCrumbs,
  activeFactory,
  sharePointDbSettingsNotFound,
  contextMenu,
  contextMenuRef,
  selectedFiles,
  isSelecting,
  selectionBox,
  uploadProgress,
  uploadPreparation,
  multiFileUpload,
  isUploading,
  itemActionInfo,
  moveOperationInProgress,
  isMovingItems,
  newFolderName,
  driveItemNewName,
  showCreateFolderModal,
  showDeleteConfirm,
  showRenameModal,
  fetchSharePointFiles,
  fetchFactorySharepointInfo,
  handleDriveItemAction,
  uploadSharepointFile,
  uploadLargeSharepointFile,
  clearFileUploadProgress,
  setMultiFileUpload,
  resetMultiFileUpload,
  getFileThumbnails,
  thumbnails,
  moveSharepointItem,
  cancelTokens,
  removeCancelToken,
  setUploadPreparation,
  clearUploadPreparation,
  getFileLogs
} = useSharepointFiles(props.orderId, props.orderFactories);

// Computed properties
const preparingUploads = computed(() => {
  return Object.entries(uploadPreparation.value)
    .filter(([fileName, preparing]) => preparing && !uploadProgress.value[fileName])
    .map(([fileName]) => ({ fileName }));
});

// Selection state
const startX = ref(0);
const startY = ref(0);
const containerRect = ref<DOMRect | null>(null);
const isRightClick = ref(false);
const ctrlKeyPressed = ref(false);
const shiftKeyPressed = ref(false);
const lastClickTime = ref(0);
const clickDelay = ref(300);
const pendingClick = ref<number | null>(null);
const draggedItems = ref<DriveItem[]>([]);
const dragOverItem = ref<DriveItem | null>(null);
const dragCounter = ref(0);
const showConflictModal = ref(false);
const conflictOperation = ref<any | null>(null);
const conflictItems = ref<DriveItem[]>([]);
const conflictTarget = ref<DriveItem | null>(null);
const showUploadCancelConfirm = ref(false);
const preventUnload = ref(false);

// Methods
const handleFactoryUpdate = (updatedFactory: Factory) => {
  activeFactory.value = updatedFactory;
  getSharePointFilesAtPath();
  fetchFactorySharepointInfo();
};

const refreshDirectory = () => {
  contextMenu.visible = false;
  getSharePointFilesAtPath();
};

const getSharePointFilesAtPath = (path = driveItemsParentInfo.value?.path) => {
  if (path) {
    driveItemsParentInfo.value = { ...driveItemsParentInfo.value, path };
    fetchSharePointFiles();
  }
};

const handleItemDoubleClick = (driveItem: DriveItem) => {
  if (pendingClick.value) {
    window.clearTimeout(pendingClick.value);
    pendingClick.value = null;
  }

  if (driveItem.type === 'folder') {
    const driveItemPath =
      driveItem.parent_info.path === '/'
        ? driveItem.name
        : `${driveItem.parent_info.path}/${driveItem.name}`;
    getSharePointFilesAtPath(driveItemPath);
  } else {
    downloadFile(driveItem);
  }
};

const downloadFile = (driveItem: DriveItem) => {
  if (!driveItem.download_url) return;

  const downloadUrl = `${driveItem.download_url}`;
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = driveItem.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const handleSharePointFilesChangeEvent = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files) {
    uploadSharePointFiles(Array.from(target.files));
  }
};

const handleSharePointFilesDropEvent = (event: DragEvent) => {
  if (event.dataTransfer?.files) {
    uploadSharePointFiles(Array.from(event.dataTransfer.files));
  }
  isDragging.value = false;
};

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  if (isUploading.value) {
    preventUnload.value = true;
    showUploadCancelConfirm.value = true;
    event.preventDefault();
    event.returnValue = '';
  }
};

const handleUploadCancelConfirm = (confirm: boolean) => {
  if (confirm) {
    cancelAllUploads();
    showUploadCancelConfirm.value = false;
    return;
  }
  showUploadCancelConfirm.value = false;
};

const cancelAllUploads = () => {
  Object.keys(uploadProgress.value).forEach((fileName) => {
    cancelUpload(String(fileName));
  });
};

const uploadSharePointFiles = (files: File[]) => {
  isUploading.value = true;

  if (files?.length > 0) {
    isOperationLoading.value = true;

    // Initialize multi-file upload tracking if there are multiple small files
    const smallFiles = files.filter((file) => file.size < 250 * 1024 * 1024);
    if (smallFiles.length > 1) {
      setMultiFileUpload(smallFiles.length);
    }
  }

  files.forEach((file) => {
    const payload = {
      destination_item_id: driveItemsParentInfo.value.id,
      file_name: file.name,
      file: file,
    };

    if (file.size >= 250 * 1024 * 1024) {
      isUploading.value = true;
      uploadLargeSharepointFile(payload)
        .then(handleUploadSuccess)
        .catch(handleUploadError)
        .finally(() => {
          isUploading.value = false;
        });
    } else {
      uploadSharepointFile(payload).then(handleUploadSuccess).catch(handleUploadError);
    }
  });
  isUploading.value = false;
  isOperationLoading.value = false;
};

const handleUploadSuccess = (response: any) => {
  if (response && response.canceled) {
    showToast('Upload canceled', 'info');
  } else {
    updateRemainingUploads();
    handleAllFilesUploaded();
  }
};

const handleUploadError = (error: any) => {
  if (axios.isCancel(error)) {
    showToast('Upload canceled', 'info');
  } else {
    updateRemainingUploads();
    handleResponseException(error);
  }
};

const updateRemainingUploads = () => {
  // Implementation of updateRemainingUploads method
};

const handleAllFilesUploaded = () => {
  if (multiFileUpload.value.completed >= multiFileUpload.value.total) {
    isOperationLoading.value = false;

    const failedCount = multiFileUpload.value.total - multiFileUpload.value.completed;
    if (failedCount > 0) {
      showToast(`${failedCount} files failed to upload`, 'error');
    }

    if (multiFileUpload.value.completed > 0) {
      showToast(`${multiFileUpload.value.completed} files uploaded successfully`, 'success');
    }

    // Add a delay before resetting the multi-file upload state
    setTimeout(() => {
      resetMultiFileUpload();
      refreshDirectory();
    }, 2000);
  }
};

const openCreateFolderModal = () => {
  showCreateFolderModal.value = true;
};

const openContextMenu = (item: DriveItem, event: MouseEvent) => {
  contextMenu.visible = true;
  contextMenu.style = {
    top: `${event.clientY}px`,
    left: `${event.clientX}px`,
  };
  contextMenu.item = selectedFiles.value.length > 0 ? selectedFiles.value : item;

  event.stopPropagation();

  nextTick(() => {
    if (contextMenuRef.value) {
      contextMenuRef.value.focus();
    }
  });
};

const closeContextMenu = (event?: Event) => {
  if (!event || !event.target || !(event.target as HTMLElement).closest('.context-menu')) {
    contextMenu.visible = false;
  }
};

const renameItem = () => {
  if (!driveItemNewName.value || !driveItemNewName.value.trim()) {
    showToast('File name cannot be empty', 'error');
    return;
  }

  driveItemNewName.value = driveItemNewName.value.trim();
  handleDriveItemAction('rename');
};

const openRenameModal = () => {
  const driveItem = contextMenu.item as DriveItem;
  if (!driveItem) return;

  let driveItemName = driveItem.name;
  let extension = '';

  if (driveItem.type === 'file') {
    extension = driveItemName.split('.').pop() || '';
    driveItemName = driveItemName.slice(0, -(extension?.length + 1));
  }

  driveItemNewName.value = driveItemName;
  contextMenu.visible = false;
  showRenameModal.value = true;
};

const showDeleteConfirmModal = () => {
  itemActionInfo.value = {
    drive_item: contextMenu.item as DriveItem | DriveItem[],
    action: 'delete',
  };
  showDeleteConfirm.value = true;
  contextMenu.visible = false;
};

const getIcon = (driveItem: DriveItem) => {
  if (!driveItem.extension) {
    driveItem.extension = 'folder';
  }

  // Define icon mapping for fallback
  const iconMapping: Record<string, string> = {
    folder: 'https://cdn-icons-png.flaticon.com/512/716/716784.png', // Folder icon
    jpg: 'https://cdn-icons-png.flaticon.com/512/136/136524.png', // JPG icon
    jpeg: 'https://cdn-icons-png.flaticon.com/512/136/136524.png', // JPG icon
    png: 'https://cdn-icons-png.flaticon.com/512/136/136524.png', // PNG icon
    zip: 'https://cdn-icons-png.flaticon.com/512/12075/12075407.png', // ZIP icon
    rar: 'https://cdn-icons-png.flaticon.com/512/2306/2306199.png', // RAR icon
    docx: 'https://cdn-icons-png.flaticon.com/512/281/281760.png', // DOCX icon
    doc: 'https://cdn-icons-png.flaticon.com/512/281/281760.png', // DOC icon
    pdf: 'https://cdn-icons-png.flaticon.com/512/337/337946.png', // PDF icon
    xlsx: 'https://cdn-icons-png.flaticon.com/512/732/732220.png', // Excel icon
    xls: 'https://cdn-icons-png.flaticon.com/512/732/732220.png', // Excel icon
    pptx: 'https://cdn-icons-png.flaticon.com/512/888/888910.png', // PowerPoint icon
    ppt: 'https://cdn-icons-png.flaticon.com/512/888/888910.png', // PowerPoint icon
    mp4: 'https://cdn-icons-png.flaticon.com/512/136/136548.png', // MP4 icon
    mp3: 'https://cdn-icons-png.flaticon.com/512/136/136545.png', // MP3 icon
    txt: 'https://cdn-icons-png.flaticon.com/512/136/136538.png', // TXT icon
    json: 'https://cdn-icons-png.flaticon.com/512/136/136528.png', // JSON icon
  };

  // Check if we should try to get a thumbnail
  if (
    ['jpeg', 'jpg', 'png', 'gif', 'bmp', 'tiff', 'pdf', 'docx', 'pptx', 'xlsx'].includes(
      driveItem.extension.toLowerCase()
    )
  ) {
    // Trigger thumbnail fetch in the background, but don't wait for it
    getFileThumbnails(driveItem);

    // Check if we already have a thumbnail
    const existingThumbnail = thumbnails.value[driveItem.id];

    if (existingThumbnail && existingThumbnail.large && existingThumbnail.large.url) {
      // Use existing thumbnail if available
      return existingThumbnail.large.url;
    }
  }

  // Fallback to icon mapping if no thumbnail
  const genericIcon = 'https://cdn-icons-png.flaticon.com/512/137/137473.png'; // Truly generic file icon
  return (
    iconMapping[driveItem.extension ? driveItem.extension.toLowerCase() : driveItem.type] ||
    genericIcon
  );
};

const toggleItemSelection = (driveItem: DriveItem) => {
  const index = selectedFiles.value.findIndex((item) => item.name === driveItem.name);
  if (index === -1) {
    selectedFiles.value.push(driveItem);
  } else {
    selectedFiles.value.splice(index, 1);
  }
};

const selectRange = (event: MouseEvent, driveItem: DriveItem) => {
  if (event.shiftKey && selectedFiles.value.length) {
    let lastSelected = selectedFiles.value[selectedFiles.value.length - 1];
    let startIndex = driveItems.value.findIndex((item) => item.name === lastSelected.name);
    let endIndex = driveItems.value.findIndex((item) => item.name === driveItem.name);

    let range = driveItems.value.slice(
      Math.min(startIndex, endIndex),
      Math.max(startIndex, endIndex) + 1
    );

    selectedFiles.value = range;
  } else {
    toggleItemSelection(driveItem);
  }
};

const handleContextMenu = (event: MouseEvent, item: DriveItem) => {
  if (selectedFiles.value.length === 0 || !selectedFiles.value.includes(item)) {
    selectedFiles.value = [item];
  }
  contextMenu.visible = true;
  contextMenu.style = {
    top: `${event.clientY}px`,
    left: `${event.clientX}px`,
  };
  contextMenu.item = selectedFiles.value.length > 1 ? selectedFiles.value : item;

  nextTick(() => {
    if (contextMenuRef.value) {
      contextMenuRef.value.focus();
    }
  });

  event.stopPropagation();
};

const handleContainerContextMenu = (event: MouseEvent) => {
  if (!(event.target as HTMLElement).closest('.file-item')) {
    contextMenu.visible = true;
    contextMenu.style = {
      top: `${event.clientY}px`,
      left: `${event.clientX}px`,
    };
    contextMenu.item = null;

    nextTick(() => {
      if (contextMenuRef.value) {
        contextMenuRef.value.focus();
      }
    });
  }
};

const startSelection = (event: MouseEvent) => {
  if (!fileContainer.value) return;

  containerRect.value = fileContainer.value.getBoundingClientRect();

  isSelecting.value = true;
  startX.value = event.clientX - containerRect.value.left;
  startY.value = event.clientY - containerRect.value.top;

  selectionBox.left = startX.value;
  selectionBox.top = startY.value;
  selectionBox.width = 0;
  selectionBox.height = 0;

  if (!ctrlKeyPressed.value && !shiftKeyPressed.value) {
    selectedFiles.value = [];
  }
};

const checkSelection = () => {
  if (!fileContainer.value || !driveItems.value.length || !containerRect.value) return;

  const fileElements = fileContainer.value.querySelectorAll('.file-item');

  const newlySelectedFiles = driveItems.value.filter((file, index) => {
    if (!fileElements[index]) return false;

    const rect = fileElements[index].getBoundingClientRect();

    const fileLeft = rect.left - containerRect.value!.left;
    const fileTop = rect.top - containerRect.value!.top;

    return (
      fileLeft < selectionBox.left + selectionBox.width &&
      fileLeft + rect.width > selectionBox.left &&
      fileTop < selectionBox.top + selectionBox.height &&
      fileTop + rect.height > selectionBox.top
    );
  });

  if (ctrlKeyPressed.value) {
    newlySelectedFiles.forEach((file) => {
      const index = selectedFiles.value.findIndex((f) => f.id === file.id);
      if (index === -1) {
        selectedFiles.value.push(file);
      } else {
        selectedFiles.value.splice(index, 1);
      }
    });
  } else {
    selectedFiles.value = ctrlKeyPressed.value
      ? [
          ...selectedFiles.value,
          ...newlySelectedFiles.filter((file) => !selectedFiles.value.includes(file)),
        ]
      : newlySelectedFiles;
  }
};

const updateSelection = (event: MouseEvent) => {
  if (!isSelecting.value || !containerRect.value) return;

  const currentX = event.clientX - containerRect.value.left;
  const currentY = event.clientY - containerRect.value.top;

  selectionBox.left = Math.min(startX.value, currentX);
  selectionBox.top = Math.min(startY.value, currentY);
  selectionBox.width = Math.abs(startX.value - currentX);
  selectionBox.height = Math.abs(startY.value - currentY);

  checkSelection();
};

const handleMouseDown = (event: MouseEvent) => {
  if (event.button === 2) {
    isRightClick.value = true;
    return;
  }

  isRightClick.value = false;

  if (
    !(event.target as HTMLElement).closest('.file-item') ||
    ctrlKeyPressed.value ||
    shiftKeyPressed.value
  ) {
    startSelection(event);
  }
};

const handleItemClick = (event: MouseEvent, item: DriveItem) => {
  if (isSelecting.value) return;

  if (pendingClick.value) {
    window.clearTimeout(pendingClick.value);
    pendingClick.value = null;
  }

  const clickTime = new Date().getTime();
  const isDoubleClick = clickTime - lastClickTime.value < clickDelay.value;
  lastClickTime.value = clickTime;

  if (isDoubleClick) {
    return;
  }

  pendingClick.value = window.setTimeout(() => {
    showFileLogs(item);
    if (ctrlKeyPressed.value) {
      const index = selectedFiles.value.indexOf(item);
      if (index === -1) {
        selectedFiles.value.push(item);
      } else {
        selectedFiles.value.splice(index, 1);
      }
      event.preventDefault();
      return;
    }

    if (shiftKeyPressed.value) {
      // If no items are selected, just select the clicked item
      if (selectedFiles.value.length === 0) {
        selectedFiles.value = [item];
        return;
      }

      const lastSelected = selectedFiles.value[selectedFiles.value.length - 1];
      const lastIndex = driveItems.value.findIndex((di) => di.id === lastSelected.id);
      const currentIndex = driveItems.value.findIndex((di) => di.id === item.id);

      // If either index is not found, just select the clicked item
      if (lastIndex === -1 || currentIndex === -1) {
        selectedFiles.value = [item];
        return;
      }

      const start = Math.min(lastIndex, currentIndex);
      const end = Math.max(lastIndex, currentIndex);

      selectedFiles.value = driveItems.value.slice(start, end + 1);
      event.preventDefault();
      return;
    }

    if (!ctrlKeyPressed.value && !shiftKeyPressed.value) {
      selectedFiles.value = [item];
    }
  }, clickDelay.value);
};

const endSelection = () => {
  isSelecting.value = false;
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Control' || event.key === 'Meta') {
    ctrlKeyPressed.value = true;
  } else if (event.key === 'Shift') {
    shiftKeyPressed.value = true;
  }
};

const handleKeyUp = (event: KeyboardEvent) => {
  if (event.key === 'Control' || event.key === 'Meta') {
    ctrlKeyPressed.value = false;
  } else if (event.key === 'Shift') {
    shiftKeyPressed.value = false;
  }
};

const handleContainerDragOver = (event: DragEvent) => {
  if (!isMovingItems.value) {
    isDragging.value = true;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }
};

const handleContainerDragLeave = (event: DragEvent) => {
  if (!isMovingItems.value && !(event.relatedTarget as HTMLElement)?.closest('.container-fluid')) {
    isDragging.value = false;
  }
};

const handleContainerDrop = (event: DragEvent) => {
  if (!isMovingItems.value) {
    handleSharePointFilesDropEvent(event);
  }
  isDragging.value = false;
  isMovingItems.value = false;
};

const handleDragLeave = (event: DragEvent, item: DriveItem) => {
  if (item && dragOverItem.value === item) {
    dragOverItem.value = null;
  }
};

const handleDragStart = (event: DragEvent, item: DriveItem) => {
  isMovingItems.value = true;
  if (pendingClick.value) {
    window.clearTimeout(pendingClick.value);
    pendingClick.value = null;
  }

  if (!selectedFiles.value.includes(item)) {
    selectedFiles.value = [item];
  }

  draggedItems.value = [...selectedFiles.value];

  if (selectedFiles.value.length > 1 && event.dataTransfer) {
    const dragImage = document.createElement('div');
    dragImage.className = 'drag-image';
    const dragCount = document.createElement('div');
    dragCount.className = 'drag-count';
    dragCount.textContent = `${selectedFiles.value.length} items`;
    dragImage.appendChild(dragCount);
    document.body.appendChild(dragImage);
    event.dataTransfer.setDragImage(dragImage, 25, 25);
    setTimeout(() => {
      document.body.removeChild(dragImage);
    }, 0);
  }

  if (event.dataTransfer) {
    event.dataTransfer.setData(
      'text/plain',
      JSON.stringify({
        action: 'move',
        items: draggedItems.value.map((item) => item.id),
      })
    );

    event.dataTransfer.effectAllowed = 'move';
  }
};

const handleDragOver = (event: DragEvent, item: DriveItem) => {
  if (item.type === 'folder' && event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move';
    dragOverItem.value = item;
  } else if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'none';
  }
};

const handleDrop = (event: DragEvent, targetFolder: DriveItem) => {
  if (targetFolder.type !== 'folder') {
    return;
  }

  dragOverItem.value = null;

  try {
    if (event.dataTransfer) {
      const data = JSON.parse(event.dataTransfer.getData('text/plain'));

      if (data.action === 'move' && data.items && data.items.length > 0) {
        const itemsToMove = draggedItems.value.filter(
          (item) =>
            !(
              item.id === targetFolder.id ||
              (item.type === 'folder' && targetFolder.parent_info.path.includes(item.name))
            )
        );

        if (itemsToMove.length === 0) {
          showToast('Cannot move a folder into itself or its subfolder', 'error');
          return;
        }

        executeMoveOperation(itemsToMove, targetFolder, false);
      }
    }
  } catch (error) {
    console.error('Error processing drop:', error);
  }
};

const handleDragEnd = () => {
  draggedItems.value = [];
  dragOverItem.value = null;
  isMovingItems.value = false;
};

const executeMoveOperation = async (
  itemsToMove: DriveItem[],
  targetFolder: DriveItem,
  forceRename = false
) => {
  if (moveOperationInProgress.value) {
    return;
  }

  moveOperationInProgress.value = true;
  isOperationLoading.value = true;

  try {
    const movePromises = itemsToMove.map((item) => {
      return moveSharepointItem(item.id, targetFolder.id, forceRename);
    });

    await Promise.all(movePromises);

    showToast(`${itemsToMove.length} item(s) moved successfully`, 'success');
    refreshDirectory();
    isDragging.value = false;
    moveOperationInProgress.value = false;
    isOperationLoading.value = false;
  } catch (error) {
    isOperationLoading.value = false;
    isDragging.value = false;
    moveOperationInProgress.value = false;

    if (error.response && error.response.status === 409) {
      handleConflict(itemsToMove, targetFolder);
    } else {
      handleResponseException(error);
    }
  }
};

const handleConflict = (itemsToMove: DriveItem[], targetFolder: DriveItem) => {
  showConflictModal.value = true;
  conflictItems.value = itemsToMove;
  conflictTarget.value = targetFolder;
};

const resolveConflict = (shouldRename: boolean) => {
  showConflictModal.value = false;

  if (shouldRename && conflictTarget.value) {
    executeMoveOperation(conflictItems.value, conflictTarget.value, true);
  } else {
    showToast('Move operation canceled', 'info');
  }

  conflictItems.value = [];
  conflictTarget.value = null;
};

const cancelUpload = (fileName: string) => {
  const cancelToken = cancelTokens.value[fileName];
  if (cancelToken) {
    cancelToken.cancel('Upload canceled by user');
    removeCancelToken(fileName);
    clearFileUploadProgress(fileName);
  }
};

const showFileLogs = async (file) => {
  emits('loading-logs');
  let fileLogs = await getFileLogs(file);

  emits('show-logs',{'logs': fileLogs, 'file': file});
  emits('loading-logs');
};

// Helper function for error handling
const handleResponseException = (error: any) => {
  console.error('API Error:', error);
  showToast(error.response?.data?.message || 'An error occurred', 'error');
};

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('click', closeContextMenu);
  document.addEventListener('mouseup', endSelection);
  document.addEventListener('keydown', handleKeyDown);
  document.addEventListener('keyup', handleKeyUp);
  window.addEventListener('beforeunload', handleBeforeUnload);

  fetchSharePointFiles();
  fetchFactorySharepointInfo();
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeContextMenu);
  document.removeEventListener('mouseup', endSelection);
  document.removeEventListener('keydown', handleKeyDown);
  document.removeEventListener('keyup', handleKeyUp);
  window.removeEventListener('beforeunload', handleBeforeUnload);
});
</script>

<style lang="scss">
.drag-image {
  position: fixed;
  top: -1000px;
  left: -1000px;
  z-index: -1;
  @apply bg-emerald-500/80 rounded-full w-[50px] h-[50px] flex items-center justify-center;
}

.drag-count {
  @apply text-white font-bold text-center;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.drive-item:hover .item-menu {
  @apply block;
}
</style>
